// Most of this code is lifted straight from plot3d.js from the UTP2 html files.
// Its just been updated to use react and simplified a little as the data is now
// adjusted in the PRPSFunctions.ts

import { useEffect, useRef } from "react";
import { PlotValue } from "../../results/Charts/PhaseResolvedFunctions";

interface Props {
  series: PlotValue[][];
}

const no_bins_x = 360; //!< How many phase angle bins
const no_bins_z = 25; //!< How many cyclces are plotted at a time
const z_angle = 60.0 * (3.141592654 / 180.0); //!< Angle of the plot
const sin_z_angle = Math.sin(z_angle); //!< Sine of the angle
const cos_z_angle = Math.cos(z_angle); //!< Cosine of the angle
const margin_bottom = 25;
const margin_top = 25;
const margin_left = 25;
const margin_right = 25;

export default function Plot3d(props: Props) {
  const canvasElement = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const ctx = canvasElement?.current?.getContext("2d");

    let origin_x = 0;
    let origin_y = 0;
    let bar_width = 0;
    let bar_depth = 0;
    let bar_height_scale = 20;
    let bar_aspect_ratio = 4;

    let x_len = 0;
    let y_len = 0;
    let z_len = 0;

    let max_val_y = props.series.flat().reduce((acc, val) => Math.max(acc, val.y), 0);
    let y_baseline = 0;

    let cur_cycle = 0;

    const prps: number[][] = [];

    const first_cycle = 0;
    const last_cycle = props.series.length;
    for (let current_cycle = first_cycle; current_cycle < last_cycle; current_cycle++) {
      // pre-fill with zeros
      let cycle = new Array<number>(360).fill(0);
      if (props.series[current_cycle]) {
        props.series[current_cycle].forEach((datum: PlotValue) => {
          cycle[datum.x] = Math.abs(datum.y);
        });
      }
      // push onto prps
      prps.push(cycle);
    }

    const draw_line = (
      base_line_start_x: number,
      base_line_start_y: number,
      base_line_end_x: number,
      base_line_end_y: number,
    ) => {
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(base_line_start_x, base_line_start_y);
        ctx.lineTo(base_line_end_x, base_line_end_y);
        ctx.stroke();
      }
    };

    // Draw a frame
    const draw_cycle = (cycle: number) => {
      if (ctx && canvasElement.current) {
        ctx.clearRect(0, 0, canvasElement.current.width, canvasElement.current.height);
        ctx.fillStyle = "rgb(0,0,0)";

        // Draw grid
        for (let x_tick = 0; x_tick <= 360; x_tick += 90) {
          // x axis lines along base of 3D plot
          const base_line_start_x = origin_x + x_tick * bar_width;
          const base_line_start_y = origin_y;
          const base_line_end_x = base_line_start_x + z_len * cos_z_angle;
          const base_line_end_y = base_line_start_y - z_len * sin_z_angle;
          draw_line(base_line_start_x, base_line_start_y, base_line_end_x, base_line_end_y);

          const label = Math.round(x_tick).toString();
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillText(label, base_line_start_x, base_line_start_y + 10);

          // x axis lines up back of 3D plot (vertical)
          const back_line_x = base_line_end_x;
          const back_line_start_y = base_line_end_y;
          const back_line_end_y = back_line_start_y + y_len;
          draw_line(back_line_x, back_line_start_y, back_line_x, back_line_end_y);
        }

        for (let z_tick = 0; z_tick <= 25; z_tick += 12.5) {
          // z axis lines across base of 3D plot (horizontal)
          const base_line_start_x = origin_x + z_tick * bar_depth * cos_z_angle;
          const base_line_end_x = base_line_start_x + x_len;
          const base_line_y = origin_y - z_tick * bar_depth * sin_z_angle;
          draw_line(base_line_start_x, base_line_y, base_line_end_x, base_line_y);
        }

        for (let y_tick = 0; y_tick <= max_val_y; y_tick += max_val_y / 4) {
          // y axis lines across back of 3D plot (horizontal)
          const back_line_start_x = origin_x + z_len * cos_z_angle;
          const back_line_end_x = back_line_start_x + x_len;
          const back_line_y = origin_y - z_len * sin_z_angle - y_tick * bar_height_scale;
          draw_line(back_line_start_x, back_line_y, back_line_end_x, back_line_y);

          const label = Math.round(y_tick + y_baseline).toString() + "  ";
          ctx.textAlign = "end";
          ctx.textBaseline = "middle";
          ctx.fillText(label, back_line_start_x, back_line_y);
        }

        // Draw bars
        ctx.fillStyle = "rgb(200,0,0)";

        for (let zdx = 0; zdx < no_bins_z; zdx++) {
          const zorigin_x = origin_x + (no_bins_z - zdx) * bar_depth * cos_z_angle;
          const zorigin_y = origin_y - (no_bins_z - zdx) * bar_depth * sin_z_angle;

          var phase_data = new Array<number>(no_bins_z).fill(0);
          if (prps[cycle + zdx]) {
            phase_data = prps[cycle + zdx];
          }
          for (let xdx = 0; xdx < 360; xdx++) {
            if (phase_data[xdx] !== 0) {
              ctx.fillRect(
                zorigin_x + xdx * bar_width,
                zorigin_y - phase_data[xdx] * bar_height_scale,
                bar_width,
                phase_data[xdx] * bar_height_scale,
              );
            }
          }
        }
      }
    };

    const draw = () => {
      // Set size to parent
      if (canvasElement.current) {
        const canvas = canvasElement.current;
        canvas.height = canvas.parentElement?.clientHeight ?? 100;
        canvas.width = canvas.parentElement?.clientWidth ?? 150;

        // Origin
        origin_x = margin_left;
        origin_y = canvas.height - margin_bottom;
        // Plot size
        const plot_height = canvas.height - margin_bottom - margin_top;
        const plot_width = canvas.width - margin_left - margin_right;

        const ratio = Math.min(Math.max(plot_width / plot_height, 2), 4);
        bar_aspect_ratio = 4 - (ratio - 2);

        // Determine bar size
        // Overall width, W = (Zbins * bar_depth * cos_angle) + (Xbins * bar_width)
        // bar_depth = bar_width * aspect_ratio
        // therefore, bar_width = W / ((Zbins * aspect_ratio * cos_angle) + Xbins)
        bar_width = plot_width / (no_bins_z * bar_aspect_ratio * cos_z_angle + no_bins_x);
        bar_depth = bar_width * bar_aspect_ratio;

        // Determine bar scale (height)
        // Overall height, H = (Zbins * bar_depth * sin_angle) + (max_y * bar_height_scale)
        // therefore, bar_height_scale = (H - (Zbins * bar_depth * sin_angle)) / max_y
        bar_height_scale = (plot_height - no_bins_z * bar_depth * sin_z_angle) / max_val_y;

        // Setup axes
        x_len = no_bins_x * bar_width; //!< Length of x axis
        y_len = -(max_val_y * bar_height_scale); //!< Length of y axis
        z_len = no_bins_z * bar_depth; //!< Length of z axis

        draw_cycle(cur_cycle);
        cur_cycle++;
        if (cur_cycle === prps.length) {
          cur_cycle = 0;
        }
      }
    };

    const handle = setInterval(() => {
      draw();
    }, 20);

    return () => {
      clearInterval(handle);
    };
  }, [props.series]);

  return <canvas ref={canvasElement} />;
}
